import { useFormContext, useWatch } from "react-hook-form";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import TextArea from "../../../../gds/TextArea";
import YesNoOptions from "../../../../../constants/options/YesNoOptions";

// Constants for comment fields
const COMMENT_CHARACTER_LIMIT = 2500;
const COMMENT_WARNING_THRESHOLD = 250;
const COMMENT_ROWS = 4;

/**
 * Reusable component for background information questions
 * Used by both PrincipalBackgroundInformation and ProducerBackgroundInformation components
 */
export default function BackgroundQuestions() {
    const { control } = useFormContext();

    // Watch all background question values to conditionally show comment fields
    const watchedValues = useWatch({ control });

    return (
        <>
            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:FiledForBankruptcy"
                control={control}
                name="hasFiledForBankruptcy"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        Have you filed for, or been discharged from any bankruptcy (including personal bankruptcy),
                        insolvency or assignment for the benefit of creditors with a filing or discharge date, whichever
                        is later, in the last five (5) years?{/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.hasFiledForBankruptcy === 'true' && (
                <TextArea
                    control={control}
                    name="hasFiledForBankruptcyComment"
                    label="Please provide additional details about your bankruptcy filing"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:DelinquentUnpaidDebts"
                control={control}
                name="hasDelinquentUnpaidDebts"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        Do you have delinquent unpaid debts exceeding, in total, $10,000? (Consumer debt, tax liens,
                        loans, child support payments, alimony payments, civil judgments, and other delinquent debt.){/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.hasDelinquentUnpaidDebts === 'true' && (
                <TextArea
                    control={control}
                    name="hasDelinquentUnpaidDebtsComment"
                    label="Please provide additional details about your delinquent debts"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:AdministrativeProceeding"
                control={control}
                name="hasBeenSubjectOfAnAdministrativeProceeding"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        With the exception of situations specific to continuing education, have you ever been the subject
                        of an administrative proceeding regarding any professional or occupational license that resulted
                        in disciplinary action?{/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.hasBeenSubjectOfAnAdministrativeProceeding === 'true' && (
                <TextArea
                    control={control}
                    name="hasBeenSubjectOfAnAdministrativeProceedingComment"
                    label="Please provide additional details about the administrative proceeding"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:LicenseSuspended"
                control={control}
                name="hasLicenseBeenSuspended"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        With the exception of situations specific to continuing education, has your insurance license ever
                        been suspended by, subject to a consent order from, revoked by, or surrendered to, any regulatory
                        agency, or have you ever been fined, penalized, sanctioned or subject to any other disciplinary
                        action by a state or federal regulatory agency or self regulatory organization or are you currently
                        under investigation as a result of your activities in the business of insurance, securities, banking,
                        investment banking, or real estate?{/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.hasLicenseBeenSuspended === 'true' && (
                <TextArea
                    control={control}
                    name="hasLicenseBeenSuspendedComment"
                    label="Please provide additional details about your license suspension or disciplinary action"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:ContractTerminatedForMisconduct"
                control={control}
                name="hasHadTerminatedContractFromMisconduct"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        Have you ever had an insurance agency contract or any other business relationship with an
                        insurance company terminated for any alleged misconduct?{/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.hasHadTerminatedContractFromMisconduct === 'true' && (
                <TextArea
                    control={control}
                    name="hasHadTerminatedContractFromMisconductComment"
                    label="Please provide additional details about the terminated contract"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:PriorConviction"
                control={control}
                name="hasPriorConviction"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        Have you ever been convicted of, pleaded guilty or no contest to, or are you currently charged
                        with or under investigation for any misdemeanor involving dishonesty or breach of trust or any felony?{/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.hasPriorConviction === 'true' && (
                <TextArea
                    control={control}
                    name="hasPriorConvictionComment"
                    label="Please provide additional details about your conviction or charges"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:SubjectOfInvestigation"
                control={control}
                name="isSubjectOfInvestigation"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        Are you now the subject of any complaint, investigation, or proceeding that could result in a YES
                        answer to any of the previous questions?{/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.isSubjectOfInvestigation === 'true' && (
                <TextArea
                    control={control}
                    name="isSubjectOfInvestigationComment"
                    label="Please provide additional details about the investigation or proceeding"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <RadioButtonGroup trackingId="shared:BackgroundInformation:BackgroundQuestions:GeicoEmployeeBusinessRelationship"
                control={control}
                name="hasBusinessRelationshipWithGEICOEmployee"
                layout="Two Column"
                options={YesNoOptions}
                legend={
                    <>
                        Do you have a business relationship with a current GEICO employee or with a captive GEICO
                        agency?{/*
                        */}<span className='required'>*</span>
                    </>
                }
            />

            {watchedValues?.hasBusinessRelationshipWithGEICOEmployee === 'true' && (
                <TextArea
                    control={control}
                    name="hasBusinessRelationshipWithGEICOEmployeeComment"
                    label="Please provide additional details about your business relationship with GEICO"
                    placeholder="Enter additional information..."
                    rows={COMMENT_ROWS}
                    maxLength={COMMENT_CHARACTER_LIMIT}
                    showCharacterCount={true}
                    characterCountMax={COMMENT_CHARACTER_LIMIT}
                    characterCountWarningThreshold={COMMENT_WARNING_THRESHOLD}
                />
            )}

            <p>
                <span className='required'>*</span> Indicates required field.
            </p>
        </>
    );
}
